import { toLower } from 'lodash/fp';
import { useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { TextKey, RouteName } from '@mews-commander/enums';
import { Service, useRepositoryQuery } from '@mews-commander/data-access';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { hasCurrentProfileAnyServicePrivileges } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { orderOrderableEntityArray } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import { isAdditionalService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { useCommanderSelector } from '@mews-commander/utils';
import { AssistantNavigation } from './AssistantNavigation';

const createResult = (
    id: string,
    routeName: RouteName,
    titleKey: Mews.TextKey,
    description: string,
    params: {},
    localizeText: ReturnType<typeof useLocalizeText>
): AssistantNavigation => {
    const title = localizeText({ textKey: titleKey });
    return {
        id: id || routeName,
        titleKey,
        routeName,
        description,
        params,
        searchValue: toLower(`${title}${description ? ` ${description}` : ''}`),
    };
};

export function getServiceResult(
    container: ReturnType<typeof getContainer>,
    service: Service,
    parentPath: string,
    localizeText: ReturnType<typeof useLocalizeText>
) {
    const { appLocalizationComponent } = container();
    const { id, names } = service;
    const params = { query: { OwnerId: id } };
    const description = appLocalizationComponent.localizeProperty(names);

    const visitDetailResult = createResult(
        'detail' + id,
        RouteName.VisitDetail,
        TextKey.Service,
        '',
        { serviceId: id },
        localizeText
    );
    const productResult = createResult(
        `${RouteName.ProductIndex}${id}`,
        RouteName.ProductIndex,
        TextKey.ProductPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.ProductPlural })}`,
        params,
        localizeText
    );

    const businessSegmentResult = createResult(
        `${RouteName.BusinessSegmentIndex}${id}`,
        RouteName.BusinessSegmentIndex,
        TextKey.BusinessSegmentPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.BusinessSegmentPlural })}`,
        params,
        localizeText
    );

    const serviceDocumentResult = createResult(
        `${RouteName.ServiceDocumentIndex}${id}`,
        RouteName.ServiceDocumentIndex,
        TextKey.ServiceDocumentPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.ServiceDocumentPlural })}`,
        params,
        localizeText
    );

    const basicServiceSearchResults = [visitDetailResult, productResult, businessSegmentResult, serviceDocumentResult];

    const resourceFeatureResult = createResult(
        `${RouteName.ResourceFeatureIndex}${id}`,
        RouteName.ResourceFeatureIndex,
        TextKey.SpaceFeaturePlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.SpaceFeaturePlural })}`,
        params,
        localizeText
    );

    const resourceCategoryResult = createResult(
        `${RouteName.ResourceCategoryIndex}${id}`,
        RouteName.ResourceCategoryIndex,
        TextKey.ResourceCategoryPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.ResourceCategoryPlural })}`,
        params,
        localizeText
    );

    const productRuleResult = createResult(
        `${RouteName.ProductRuleIndex}${id}`,
        RouteName.ProductRuleIndex,
        TextKey.ProductRulePlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.ProductRulePlural })}`,
        params,
        localizeText
    );

    const rateResult = createResult(
        `${RouteName.RateIndex}${id}`,
        RouteName.RateIndex,
        TextKey.RatePlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.RatePlural })}`,
        params,
        localizeText
    );

    const rateGroupResult = createResult(
        `${RouteName.RateGroupIndex}${id}`,
        RouteName.RateGroupIndex,
        TextKey.RateGroupPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.RateGroupPlural })}`,
        params,
        localizeText
    );

    const restrictionResult = createResult(
        `${RouteName.RestrictionIndex}${id}`,
        RouteName.RestrictionIndex,
        TextKey.RestrictionPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.RestrictionPlural })}`,
        params,
        localizeText
    );

    const travelAgencyContractResult = createResult(
        `travelAgencyContract${id}`,
        RouteName.TravelAgencyContractReportIndex,
        TextKey.TravelAgencyContractPlural,
        `${parentPath}${description}`,
        { query: { 'Service.Id': id } },
        localizeText
    );

    const voucherResult = createResult(
        `${RouteName.VoucherIndex}${id}`,
        RouteName.VoucherIndex,
        TextKey.VoucherPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.VoucherPlural })}`,
        params,
        localizeText
    );

    const bookingEngineResult = createResult(
        `${RouteName.BookingEngineIndex}${id}`,
        RouteName.BookingEngineIndex,
        TextKey.BookingEnginePlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.BookingEnginePlural })}`,
        params,
        localizeText
    );

    const kioskResult = createResult(
        `${RouteName.KioskIndex}${id}`,
        RouteName.KioskIndex,
        TextKey.KioskPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.KioskPlural })}`,
        params,
        localizeText
    );

    const ageCategoryResult = createResult(
        `${RouteName.AgeCategoryIndex}${id}`,
        RouteName.AgeCategoryIndex,
        TextKey.AgeCategoryPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.AgeCategoryPlural })}`,
        params,
        localizeText
    );

    const promotedServiceAssignmentResult = createResult(
        `${RouteName.PromotedServiceAssignmentIndex}${id}`,
        RouteName.PromotedServiceAssignmentIndex,
        TextKey.PromotedServiceAssignmentPlural,
        `${parentPath}${description} / ${localizeText({ textKey: TextKey.PromotedServiceAssignmentPlural })}`,
        params,
        localizeText
    );

    const registrationCardConfigurationDetailResult = createResult(
        `registrationCardConfiguration${id}`,
        RouteName.RegistrationCardConfigurationDetail,
        TextKey.RegistrationCardConfiguration,
        `${parentPath}${description}`,
        { id },
        localizeText
    );

    if (isAdditionalService(service)) {
        return basicServiceSearchResults;
    }

    return [
        ...basicServiceSearchResults,
        resourceFeatureResult,
        resourceCategoryResult,
        productRuleResult,
        rateResult,
        rateGroupResult,
        restrictionResult,
        travelAgencyContractResult,
        voucherResult,
        bookingEngineResult,
        kioskResult,
        ageCategoryResult,
        promotedServiceAssignmentResult,
        registrationCardConfigurationDetailResult,
    ];
}

export const useServicesNavigation = () => {
    const canConfigureService = useCommanderSelector((state) => hasCurrentProfileAnyServicePrivileges(state, {}));
    const enterpriseId = useCurrentEnterpriseId();
    const container = useCommanderSelector(getContainer);
    const result = useRepositoryQuery('services', 'getAll', {
        enterpriseIds: [enterpriseId],
    });

    const services = result.data ? result.data.services : [];
    const localizeText = useLocalizeText();
    const parentPath = `${localizeText({ textKey: TextKey.ServicePlural })}${UnicodeEntity.Space}${
        UnicodeEntity.ForwardSlash
    }${UnicodeEntity.Space}`;

    if (!canConfigureService || !services) {
        return [];
    }

    const orderedServices = orderOrderableEntityArray(services);
    return orderedServices?.flatMap((service) => getServiceResult(container, service, parentPath, localizeText));
};
